<template>
  <main class="recommended-users-page">
    <AppNavBar
      background-color="#fff"
      :title="t('invite.recommendedUsers')"
      :border="false"
    />

    <div class="recommended-users-tabs" role="tablist">
      <button
        type="button"
        :class="{ 'is-active': activeType === 'trading' }"
        @click="activeType = 'trading'"
      >
        {{ t("invite.tradingFriends") }}
      </button>
      <button
        type="button"
        :class="{ 'is-active': activeType === 'registered' }"
        @click="activeType = 'registered'"
      >
        {{ t("invite.registeredFriends") }}
      </button>
    </div>

    <van-pull-refresh v-model="refreshing" @refresh="refreshList">
      <van-list
        v-model:loading="loading"
        :finished="finished"
        :finished-text="''"
        :immediate-check="false"
        @load="loadList"
      >
        <section v-if="displayList.length" class="recommended-users-list">
          <article
            v-for="item in displayList"
            :key="item.id"
            class="recommended-user"
          >
            <div class="recommended-user__main">
              <div class="recommended-user__title">
                {{ recordTitle(item) }}
              </div>
              <span>{{ formatTime(item.rewardAt || item.registerAt) }}</span>
            </div>
            <div v-if="item.amountText" class="recommended-user__amount">
              {{ item.amountText }}
            </div>
          </article>
        </section>

        <van-empty
          v-else-if="!loading"
          class="recommended-users-empty"
          :image="emptydata"
          :description="t('invite.noRecommendedUsers')"
        />
      </van-list>
    </van-pull-refresh>
  </main>
</template>

<script setup>
import dayjs from "dayjs";
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { refFriendList } from "@/api/wallet";
import emptydata from "@/assets/images/emptydata_gray.png";

const { t } = useI18n();
const REWARD_TYPE_TEXT_KEYS = Object.freeze({
  1: "invite.cardOpeningCommission",
  2: "invite.spendingCommission",
  3: "invite.commissionRefundDeduction",
  4: "invite.reservedReward",
  10: "invite.courseCommission",
  11: "invite.globalDirectorCourseCommission",
  12: "invite.globalDirectorCardOpeningCommission",
  13: "invite.teamBonusPoolCardOpeningCommission",
  14: "invite.teamBonusPoolCourseCommission",
  15: "invite.globalDirectorTeamCardOpeningCommission",
  16: "invite.globalDirectorTeamCourseCommission",
});
const activeType = ref("trading");
const loading = ref(false);
const refreshing = ref(false);
const displayList = ref([]);
const pageNum = ref(1);
const finished = ref(false);
const requesting = ref(false);
const pageSize = 20;
let listVersion = 0;
let queuedReset = false;

function normalizeText(value) {
  if (value === null || value === undefined) {
    return "";
  }
  const text = String(value).trim();
  return !text ||
    text.toLowerCase() === "null" ||
    text.toLowerCase() === "undefined"
    ? ""
    : text;
}

function formatAmount(value, symbol) {
  const amount = normalizeText(value);
  if (!amount) {
    return "";
  }
  return `${amount} ${normalizeText(symbol)}`.trim();
}

function normalizeFriendRecord(item = {}, listType = currentListType()) {
  const friendName = normalizeText(item.friendName);
  const rewardType = Number(item.rewardType);
  return {
    ...item,
    id: item.id ?? `${friendName}-${item.rewardAt || ""}-${rewardType}`,
    friendName,
    cardType: normalizeText(item.cardType),
    rewardAt: item.rewardAt,
    rewardType,
    listType,
    amountText: formatAmount(item.rewardAmount, item.symbol),
  };
}

function recordTitle(item) {
  const friendName = item.friendName || "--";
  if (item.listType === 2) {
    return t("invite.rewardRecordRegistrationTitle", { friendName });
  }
  const textKey = REWARD_TYPE_TEXT_KEYS[Number(item.rewardType)];
  return textKey ? t(textKey) : "--";
}

function formatTime(value) {
  if (value === undefined || value === null || value === "") {
    return "--";
  }

  const numericValue = Number(value);
  const dateValue = Number.isFinite(numericValue)
    ? numericValue < 10000000000
      ? numericValue * 1000
      : numericValue
    : value;
  const date = dayjs(dateValue);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "--";
}

function currentListType() {
  return activeType.value === "registered" ? 2 : 1;
}

async function loadList(reset = false) {
  if (reset) {
    listVersion += 1;
    displayList.value = [];
    pageNum.value = 1;
    finished.value = false;
  }

  if (requesting.value) {
    queuedReset = queuedReset || reset;
    return;
  }

  if (!reset && finished.value) {
    return;
  }

  const requestVersion = listVersion;
  const requestPageNum = pageNum.value;
  const requestListType = currentListType();
  requesting.value = true;
  loading.value = true;
  try {
    const result = await refFriendList(
      {
        listType: requestListType,
        pageNum: requestPageNum,
        pageSize,
      },
      { meta: { showLoading: false } },
    );
    const rows = Array.isArray(result?.list)
      ? result.list.map((item) => normalizeFriendRecord(item, requestListType))
      : [];
    const responsePageNum = Number(result?.pageNum || requestPageNum);
    const responsePages = Number(result?.pages || 0);

    if (requestVersion !== listVersion) {
      return;
    }

    displayList.value =
      requestPageNum === 1 ? rows : [...displayList.value, ...rows];
    pageNum.value = responsePageNum + 1;
    finished.value =
      responsePages > 0
        ? responsePageNum >= responsePages
        : rows.length < pageSize;
  } catch (error) {
    // The HTTP client already displays the request error.
  } finally {
    requesting.value = false;
    loading.value = false;
    if (queuedReset) {
      queuedReset = false;
      void loadList(true);
    }
  }
}

async function refreshList() {
  await loadList(true);
  refreshing.value = false;
}

watch(activeType, () => {
  void loadList(true);
});

onMounted(() => {
  void loadList(true);
});
</script>

<style scoped lang="scss">
.recommended-users-page {
  min-height: var(--app-viewport-height);
  background: #ffffff;
}

.recommended-users-tabs {
  position: sticky;
  top: calc(var(--van-nav-bar-height) + env(safe-area-inset-top) + 20px);
  z-index: 20;
  display: flex;
  height: 44px;
  margin: 20px 20px 0;
  padding: 3px;
  border-radius: 24px;

  background: #ececec;
  box-shadow: 0 8px 0 #fff;

  button {
    flex: 1;
    min-width: 0;
    border: 0;
    border-radius: 21px;
    background: transparent;
    color: #222222;
    font-size: 16px;
    font-weight: 500;
  }

  button.is-active {
    background: #ffffff;
  }
}

.recommended-users-list {
  padding: 22px 20px 24px;
}

.recommended-user {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 80px;
  margin-bottom: 10px;
  padding: 12px 18px;
  box-sizing: border-box;
  gap: 16px;
  border-radius: 16px;
  background: #f5f5f5;
}

.recommended-user__main {
  min-width: 0;
}

.recommended-user__title {
  overflow: hidden;
  color: #000;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
}

.recommended-user__main > span {
  display: block;
  margin-top: 3px;
  color: #999999;
  font-size: 14px;
  line-height: 20px;
}

.recommended-user__amount {
  flex: none;
  color: var(--wp-color-theme);
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;
}

.recommended-users-loading,
.recommended-users-empty {
  display: flex;
  justify-content: center;
  padding-top: 80px;
}

.recommended-users-empty {
  display: flex;
  justify-content: center;
  :deep(.van-empty__image) {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }
}
</style>
